import React from 'react';
import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useGetRiskFactors } from './hooks/useGetRiskFactors';
import { useResolution886Report } from '@/context/Resolution886Report';
import { RiskBadge } from '@/components/views/Resolution886/components/RiskBadge';
import type { ColumnsType } from 'antd/lib/table';
import * as S from './styles';

const riskFactorWidth = 200;
const exposureTimeWidth = 225;
const taskColumnWidth = 150;
const SKELETON_ROWS = 4

export function RiskFactorsTable(): JSX.Element {
	const { report } = useResolution886Report();
	const { isLoading, riskFactors, taskKeys, taskNames } = useGetRiskFactors(report);

	const showScroll = taskKeys.length > 3;
	const riskLevelWidth = taskKeys.length * taskColumnWidth;
	const scrollWidth = riskFactorWidth + exposureTimeWidth + riskLevelWidth;

	function getTaskName(taskNames: string[], index: number): string {
		return taskNames[index] || (I18n.get('Task') + ` ${index + 1}`);
	}

	const columns: ColumnsType<object> = [
		{
			title: I18n.get('Risk factor'),
			key: 'risk_factor_group',
			align: 'center',
			children: [
				{
					title: '',
					dataIndex: 'categoryName',
					key: 'risk_factor',
					align: 'center' as const,
					width: riskFactorWidth,
					fixed: showScroll ? 'left' : undefined,
					render: (name: string) => name || 'N/A'
				}
			]
		},
		{
			title: I18n.get('Total exposure time'),
			key: 'total_exposure_time_group',
			align: 'center',
			children: [
				{
					title: '',
					dataIndex: 'totalExposureTime',
					key: 'total_exposure_time',
					align: 'center' as const,
					width: exposureTimeWidth,
					render: (time: string) => time || '0%'
				}
			]
		},
		{
			title: I18n.get('Risk level'),
			key: 'risk_level_group',
			align: 'center',
			children: taskKeys.map((taskKey: string, index: number) => ({
				title: <S.TaskTitleWrapper>{getTaskName(taskNames, index)}</S.TaskTitleWrapper>,
				dataIndex: taskKey,
				key: taskKey,
				align: 'center' as const,
				width: taskColumnWidth,
				render: (riskLevel: string) => <RiskBadge risk={riskLevel} />
			}))
		}
	];

	return (
		<Row gutter={[32, 16]}>
			<Col span={24}>
				<S.TaskTitleContainer>
					<S.TaskTitle>
						{I18n.get('Attachment I - Spreadsheet 1: Identification of risk factors')}
					</S.TaskTitle>
				</S.TaskTitleContainer>
				{isLoading ? (
					<S.SkeletonContainer>
						{[...Array.from({length: SKELETON_ROWS})].map((_, rowIndex: number) => (
							<S.SkeletonRow gutter={16} key={rowIndex}>
								<S.SkeletonCol span={8}>
									<S.SkeletonInput active size="small" />
								</S.SkeletonCol>
								<S.SkeletonCol span={8}>
									<S.SkeletonInput active size="small" />
								</S.SkeletonCol>
								<S.SkeletonCol span={8}>
									<S.SkeletonInputWide active size="small" />
								</S.SkeletonCol>
							</S.SkeletonRow>
						))}
					</S.SkeletonContainer>
				) : (
					<S.StyledTable
						rowKey="key"
						columns={columns as ColumnsType<object>}
						pagination={false}
						loading={isLoading}
						dataSource={riskFactors}
						className={showScroll ? 'scroll-enabled' : ''}
						scroll={showScroll ? { x: scrollWidth } : undefined}
					/>
				)}
			</Col>
		</Row>
	);
}
